import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { AuthService, StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../../../core/auth';
import { EntriesState } from '../../../../core/features/store/base-feature-state';
import { CartType, TinyCart } from '../../../../core/model';
import { TinyCartFacade } from '../facades';
import { CartActions } from '../store/actions';
import { StateWithCart } from '../store/cart-state';
import { CartSelectors } from '../store/selectors';

@Injectable()
export class TinyCartService implements TinyCartFacade {
  readonly cartTypesOrder: CartType[] = [
    CartType.stock,
    CartType.sample,
    CartType.freeStock,
    CartType.freeStockFillup,
    CartType.invoicedStock,
    CartType.invoicedStockFillup,
    CartType.consignmentStock,
    CartType.consignmentStockFillup,
  ];

  constructor(private store: Store<StateWithCart>, private authService: AuthService, private userIdService: UserIdService) {}

  private loadTinyCarts(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new CartActions.LoadTinyCarts(userId)),
      () => {}
    );
  }

  hasMultipleCarts(): Observable<boolean> {
    const cartEntries = this.getTinyCarts().pipe(
      map((carts) => carts?.filter((cart) => cart.numberOfEntries > 0 || cart.cartType === CartType.stock))
    );

    return cartEntries.pipe(map((entries) => entries?.length > 1 || false));
  }

  getTinyCarts(): Observable<TinyCart[] | undefined> {
    return this.store.pipe(
      select(CartSelectors.getTinyCartsState),
      withLatestFrom(this.authService.isUserLoggedIn()),
      tap(([loaderState, isUserLoggedIn]) => {
        if (isUserLoggedIn && this.needsToLoad(loaderState)) {
          this.loadTinyCarts();
        }
      }),
      map(([loaderState, isUserLoggedIn]) => {
        if (isUserLoggedIn && loaderState.value) {
          return Object.values(loaderState.value);
        } else {
          return [];
        }
      }),
      map((tinyCarts) => tinyCarts.filter((tinyCart): tinyCart is TinyCart => Boolean(tinyCart))),
      map((tinyCarts) => tinyCarts.filter((tinyCart) => tinyCart.numberOfEntries > 0 || tinyCart.cartType === CartType.stock)),
      map((tinyCarts) => this.sortTinyCarts(tinyCarts))
    );
  }

  getTinyCartsLoading(): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getTinyCartsLoading));
  }

  getTinyCartsLoaded(): Observable<boolean> {
    return this.store.pipe(select(CartSelectors.getTinyCartsLoaded));
  }

  getTinyCart(cartType: CartType): Observable<TinyCart | undefined> {
    return this.store.pipe(select(CartSelectors.getTinyCart, { cartType }));
  }

  private sortTinyCarts(tinyCarts: TinyCart[]): TinyCart[] {
    return tinyCarts?.sort((a, b) =>
      this.cartTypesOrder.indexOf(a.cartType) < this.cartTypesOrder.indexOf(b.cartType) ? -1 : 1
    );
  }

  private needsToLoad(loaderState: StateUtils.LoaderState<EntriesState<TinyCart>>) {
    return !loaderState.success && !loaderState.error && !loaderState.loading;
  }
}
